import { FC } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { grid, GridProps } from "styled-system";

import {
  FeaturedCardContent,
  FeaturedCardVariant,
} from "../FeaturedCard/FeaturedCard";
import FeaturedCard from "../FeaturedCard/FeaturedCard";
import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";
import { VerticalSpaceProps } from "../types";

type GridColumnsOptions = 2 | 3 | 4;
export interface CardPanelProps extends VerticalSpaceProps {
  /**
    - The variant of the card. Accepts one of the following values:
    - FullImageCard
    - FullImageSmallCard
    - HalfImageCard
    - HalfImageWideCard
    - NoImageCard
    - NoImageWideCard
    - IconCard
    - IconWideCard
    */
  variant: FeaturedCardVariant;
  /**
    - The number of columns. <b> NOT SUPPORTED FOR THE FOLLOWING VARIANTS: </b>
    - FullImageCard
    - FullImageSmallCard
    - HalfImageWideCard
    - NoImageWideCard
    - IconWideCard
    */
  columns?: GridColumnsOptions;
  /**    - Represents the content of the cards. Accepts an array of FeaturedCardContent. FeaturedCardContent has the following props:
  - id : string; // unique identifier of the card
  - linkUrl: string;
  - shouldOpenInNewWindow?: boolean;
  - icon?: {
    name: "map-signs";
    weight: "regular" | "solid" | "light";
    color? : string;
  };
  - image?: { url: string; alt: string; overlay?: 0 | 0.25 | 0.5 | 0.75 };
  - headline?: string;
  - description?: string;
  - moreInfo?: {
    label: string;
    visible: boolean;
  };
  - analytics?: AnalyticsProps; //Used for adobe tracking. Accepts a json object with the following props:
    - eventAction?: string; // Name assigned to the web page element, whose users' interaction you want to track.
    - eventSection?: string; // Name of the page section in which the element will sit.
    - eventType?: string; // Name assigned to categorise the action/label further to give more detail.
    - eventValue?: string; // The numerical value assigned to the event you want to track.
   */
  cardsContent: FeaturedCardContent[];
}

const GridPanel = styled(ResponsiveLayout)<GridProps>`
  display: grid;
  grid-column-gap: 20px;
  grid-row-gap: 20px;
  ${css({
    overflow: "visible",
    "& > div:first-child": {
      alignItems: "stretch",
    },
  })}
  ${grid}
`;

const getGridProps = ({
  isWideCard,
  columns,
}: {
  isWideCard: boolean;
  columns: GridColumnsOptions;
}) => {
  const customBreakPointCss = `
@media only screen and (min-width: 1025px) {
  grid-column: span ${12 / columns};
};
`;

  const wideCardCustomBreakpointCss = `
@media only screen and (min-width: 416px) and (max-width: ${
    isWideCard ? "660px" : "460px"
  }) {
  grid-column: span 12;
};`;

  return {
    gridColumn: {
      _: `span 4`,
      tablet: "span 6",
    },
    css: customBreakPointCss + wideCardCustomBreakpointCss,
  };
};

const renderCards = ({
  cardsContent,
  variant,
  columns = 4,
}: CardPanelProps): JSX.Element[] => {
  const isWideCard =
    variant === "IconWideCard" || variant === "NoImageWideCard";

  return cardsContent.map((cardContent: FeaturedCardContent) => {
    const gridProps = getGridProps({ isWideCard, columns });

    return (
      <FeaturedCard
        content={cardContent}
        variant={variant}
        key={cardContent.id}
        width="auto"
        marginX={0}
        marginY={0}
        {...gridProps}
      ></FeaturedCard>
    );
  });
};

const getSupportedColumns = (
  variant: FeaturedCardVariant,
  columns: GridColumnsOptions
) => {
  switch (variant) {
    case "IconWideCard":
    case "NoImageWideCard":
    case "HalfImageWideCard": {
      return 2;
    }
    case "FullImageCard":
    case "FullImageSmallCard": {
      return 3;
    }
    default: {
      return columns;
    }
  }
};

const CardPanel: FC<CardPanelProps> = ({
  cardsContent,
  variant,
  columns = 4,
  ...props
}) => {
  const supportedColumns = getSupportedColumns(variant, columns);

  const cardsList = renderCards({
    cardsContent,
    variant,
    columns: supportedColumns,
  });

  return (
    <GridPanel
      gridTemplateColumns={{
        tablet: "repeat(12, 1fr)",
        _: "repeat(4, 1fr)",
      }}
      {...props}
    >
      {cardsList}
    </GridPanel>
  );
};

export default CardPanel;
